import { useThemeVariant } from "@/contexts/ThemeContext";
import { Skel } from "@/components/home/TenantSkeletons";
import { useHeroModel, useTrustItems } from "@/components/home/layouts/models";
import { ClassicTrust } from "@/components/home/layouts/classic";
import { MinimalTrust } from "@/components/home/layouts/minimal";
import { WarmTrust } from "@/components/home/layouts/warm";
import { BoldStats } from "@/components/home/layouts/bold";
import { ElegantTrust } from "@/components/home/layouts/elegant";
import { FreshTrust } from "@/components/home/layouts/fresh";
import { ModernTrust } from "@/components/home/layouts/modern";
import { cn } from "@/lib/utils";

/**
 * Trust strip under the hero: the hero badges the tenant edits in the visual
 * editor, plus a "local team" item built from their service areas. Layout
 * follows the theme preset (stats blocks, pill row, overlapping cards…).
 */
export function TrustStrip() {
  const variant = useThemeVariant();
  const { pending, items } = useTrustItems();
  const hero = useHeroModel();

  if (pending) {
    return (
      <section aria-hidden className="relative border-y tp-hairline bg-card/60">
        <div className="container mx-auto px-5 md:px-6">
          <ul className="tp-skeleton grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((index) => (
              <li key={index} className={cn("flex items-center gap-4 py-5 sm:py-7 lg:px-8 lg:first:pl-0", index > 0 && "border-t tp-hairline sm:border-t-0", index % 2 === 1 && "sm:border-l sm:pl-6", index >= 2 && "hidden sm:flex sm:border-t lg:border-t-0", index > 0 && "lg:border-l")}>
                <Skel className="h-11 w-11 shrink-0 rounded-[calc(var(--tp-radius-card)*0.55)]" />
                <span className="flex-1 space-y-2">
                  <Skel className="h-3.5 w-28 tp-skel-round" />
                  <Skel className="h-3 w-20 tp-skel-round" />
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    );
  }

  if (items.length === 0) return null;

  switch (variant) {
    case "modern-minimal": return <MinimalTrust items={items} />;
    case "warm-welcome": return <WarmTrust items={items} />;
    case "bold-vibrant": return <BoldStats m={hero} items={items} />;
    case "elegant-serif": return <ElegantTrust items={items} />;
    case "fresh-natural": return <FreshTrust items={items} />;
    case "modern": return <ModernTrust items={items} />;
    default: return <ClassicTrust items={items} />;
  }
}
